/**
 * Brand registry — Feb 2026.
 *
 * Single source of truth for every white-label variant. Add entries here
 * to onboard new demo clients; no other file needs to change. Each brand
 * exposes:
 *
 *   id           — matches the `?brand=` param value + localStorage key
 *   name         — short label (used in the "White-Label Active" chip)
 *   portalTitle  — full portal / dashboard title + tagline
 *   tagline      — sub-heading under the greeting on Launchpad
 *   wordmark     — { text, subtitle } used by the built-in text mark
 *   logoUrl      — optional CDN URL. When present, the img takes over
 *                  the wordmark. Copyright-safe: text mark by default,
 *                  swap in a licensed asset without a code change.
 *   accent       — primary hex (buttons, active-nav border, links)
 *   accentDim    — hover / pressed state
 *   accentSoft   — semi-transparent chip backgrounds
 *   polish       — { spacious?: bool, corners?: 'sharp'|'default' }
 *                  toggles the corporate-clean visual polish
 */

export const BRANDS = {
  default: {
    id: "default",
    name: "Whiplash",
    portalTitle: "Whiplash Film & Creative",
    tagline: "Every Whiplash Film & Creative tool, one screen. Pick your weapon.",
    wordmark: null, // Falls through to the existing Whiplash image
    logoUrl:
      "https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png",
    accent: "#F59E0B",       // amber-500
    accentDim: "#D97706",    // amber-600
    accentSoft: "rgba(245, 158, 11, 0.15)",
    polish: {},
  },
  quad: {
    id: "quad",
    name: "Quad",
    portalTitle: "QUAD Project Management Portal",
    tagline: "Every marketing surface, one command center.",
    wordmark: { text: "QUAD", subtitle: "PROJECT MANAGEMENT" },
    logoUrl: "https://customer-assets-0z36b82j.emergentagent.net/job_b1eec6bc-1093-44ff-b714-dbe347c2ba49/artifacts/6x6wbccg_Logo-QuadFullLockup-BW.png",
    // Iter 50 pivot — Subtle Elite. Sidebar + canvas inherit the default
    // Whiplash Dark foundation. Brand identity comes from the logo up top
    // + the accent color running through buttons, borders, icons, and
    // the active-nav pill.
    mode: "dark",
    accent: "#00A7B5",       // Quad Teal
    accentDim: "#00838F",
    accentSoft: "rgba(0, 167, 181, 0.15)",
    polish: { spacious: true, corners: "sharp" },
  },
  bsn: {
    id: "bsn",
    name: "BSN Sports",
    portalTitle: "BSN Sports Command Center",
    tagline: "Every team, every uniform, every deadline — one field of play.",
    wordmark: { text: "BSN", subtitle: "SPORTS · COMMAND" },
    logoUrl: "https://customer-assets-0z36b82j.emergentagent.net/job_b1eec6bc-1093-44ff-b714-dbe347c2ba49/artifacts/irqwfz47_BSN_logo_Underneath_1color_White_Vertical.png",
    logoScale: 1.5,
    mode: "dark",
    accent: "#E30613",       // BSN Red
    accentDim: "#B00510",
    accentSoft: "rgba(227, 6, 19, 0.15)",
    polish: { spacious: true, corners: "sharp" },
  },
  bps: {
    id: "bps",
    name: "Bass Pro Shops CLUB",
    portalTitle: "Bass Pro Shops CLUB Command Center",
    tagline: "Every store, every event, every campaign — one outfitter.",
    wordmark: { text: "BASS PRO", subtitle: "SHOPS · CLUB" },
    logoUrl: "https://customer-assets-0z36b82j.emergentagent.net/job_b1eec6bc-1093-44ff-b714-dbe347c2ba49/artifacts/9k6vnqxt_BassProCLUB_Logo.png",
    logoScale: 1.6,
    mode: "dark",
    accent: "#e2a746",       // Bass Pro gold
    accentDim: "#b3822e",
    accentSoft: "rgba(226, 167, 70, 0.18)",
    polish: { spacious: true, corners: "sharp" },
  },
};

/** Resolve the active brand from (in priority order):
 *   1. explicit URL param  ?brand=quad
 *   2. localStorage override (persisted from a previous URL param)
 *   3. email domain suffix (@quad.com → quad, @bsnsports.com → bsn,
 *                            @basspro.com → bps)
 *   4. default
 */
export function resolveBrandId({ urlParam, storedOverride, email }) {
  const norm = (v) => (v || "").toString().trim().toLowerCase();
  const inRegistry = (id) => id && BRANDS[id];

  if (inRegistry(norm(urlParam))) return norm(urlParam);
  if (inRegistry(norm(storedOverride))) return norm(storedOverride);

  // Domain-based mapping — used for onboarding whole tenants.
  const nEmail = norm(email);
  const domain = nEmail.split("@")[1] || "";
  const domainMap = {
    "quad.com": "quad",
    "bsnsports.com": "bsn",
    "basspro.com": "bps",
  };
  const byDomain = domainMap[domain];
  if (inRegistry(byDomain)) return byDomain;

  return "default";
}
